import { useEffect, useRef, useState } from 'react';

interface PollingOptions {
  /** Milliseconds between polls. ADR 0001 sizes this at 3-5s. */
  intervalMs?: number;
  enabled?: boolean;
}

/**
 * Polls an endpoint on an interval while enabled (US-4.2).
 *
 * ADR 0001 chose polling over WebSockets to stay on the Workers free tier, so
 * this is the live-sync transport. Polling pauses while the tab is hidden and
 * fires immediately on wake, which is also what makes reconnect catch-up work:
 * the next poll returns the presenter's current position, whatever was missed.
 */
export function usePolling<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  { intervalMs = 4000, enabled = true }: PollingOptions = {},
): { data: T | null; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!enabled) return;

    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const tick = async () => {
      if (!active || document.hidden) return schedule();
      try {
        const next = await fetcherRef.current(controller.signal);
        if (!active) return;
        setData(next);
        setError(null);
      } catch (cause) {
        if (active && !controller.signal.aborted) {
          setError(cause instanceof Error ? cause.message : 'Lost contact with the session.');
        }
      }
      schedule();
    };

    const schedule = () => {
      if (active) timer = setTimeout(() => void tick(), intervalMs);
    };

    const onVisible = () => {
      if (!document.hidden) void tick();
    };

    void tick();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      controller.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, intervalMs]);

  return { data, error };
}
